import _merge from 'lodash-es/merge';
import { $ } from './utils/querySelector';
import { ResizeListener } from './resize-listener';
import languages from './translateLanguages';

const DEFAULT_OPTIONS = {
    mobileTextProperty: 'lang',  // 'code',
    desktopTextProperty: 'lang',
    defaultLanguageCode: 'de',
    mobilePlaceholder: '',
    desktopPlaceholder: '',
    languageSelectElementSelector: '.ghwp-language-select',
    languageSelectOptionClass: 'ghwp-language-select__option',
    desktopBreakpoint: 992,
    languages: languages || [],
    useIconAsPlaceholder: true,
};

export function TranslateFactory(consentManager, resizeListener = null, userOptions = {}) {
    // only run google translate with user consent
    new Translate(resizeListener, userOptions);
    consentManager
        .withConsent('Google Translate', () => {
            console.log('init Translate');
            new Translate(resizeListener, userOptions);
        })
        .then();
}

class Translate {
    constructor(resizeListener = null, userOptions = {}) {
        this.currentMode = null;
        this.resizeListener = resizeListener || new ResizeListener();
        this.defaults = {};
        this.options = {};
        this.parseOptions(userOptions);
        this.init();
    }

    init() {
        const selectElement = this.getElement();
        if (!selectElement) return null;
        const observer = new MutationObserver(mutations => {
            mutations.forEach(item => {
                if (item.attributeName === 'lang') this.renderSelectOptions();
            });
        });
        observer.observe(document.documentElement, {attributes: true});
        selectElement.onchange = (e) => {
            const selectedLang = e.target.value.toLowerCase();
            e.preventDefault();
            e.target.blur();
            this.doTranslate(selectedLang);
            this.renderSelectOptions(selectedLang);
        };
        this.resizeListener.subscribe('resize', () => this.renderSelectOptions());
        this.renderSelectOptions();
    }

    getElement() {
        if (!this.languageSelectElement) {
            this.languageSelectElement = $()(this.options.languageSelectElementSelector);
        }
        return this.languageSelectElement;
    }

    getLanguageTextByCode(code) {
        const langObj = this.options.languages.find(lang => lang.code === code.toUpperCase());
        return langObj.lang;
    }

    updateSelectElementMaxWidth(lang) {
        if (this.options.useIconAsPlaceholder) {
            this.getElement().style.maxWidth = '68px'
            return
        }
        let displayedTextLength = 2;
        if (this.currentMode !== 'mobile') {
            const currentLanguage = this.getCurrentLanguage();
            if (lang || currentLanguage !== this.getDefaults('defaultLanguageCode')) {
                const displayedText = this.getLanguageTextByCode(lang || currentLanguage);
                displayedTextLength =  displayedText.length;
            } else {
                displayedTextLength = this.getPlaceholderText().length;
            }
        }
        this.getElement().style.maxWidth = `calc(68px + ${displayedTextLength}ch)`;
    }

    getDefaults(propName) {
        if (!this.defaults[propName]) {
            const defaultOption = this.getElement().dataset[propName];
            this.defaults[propName] = defaultOption || this.options[propName];
        }
        return this.defaults[propName];
    }

    getNextMode() {
        const screenWidth = this.resizeListener.getCurrentViewportWidth();
        return screenWidth < this.options.desktopBreakpoint ? 'mobile' : 'desktop';
    }

    getPlaceholderText() {
        return this.getDefaults(`${this.getNextMode()}Placeholder`);
    }

    getCurrentLanguage() {
        const lang = document.documentElement.lang;
        return lang && lang.slice(0, 2) || this.getDefaults('defaultLanguageCode');
    }

    getPlaceholderOption(lang, mode) {
        if (this.options.useIconAsPlaceholder) {
            return '<option value="" disabled selected hidden></option>'
        }
        const isDefaultLanguage = this.getCurrentLanguage() === this.getDefaults('defaultLanguageCode');
        if (!lang && isDefaultLanguage) {
            return `<option value="" disabled selected hidden>${this.getPlaceholderText()}</option>`;
        }
        const langText = lang ? lang.toUpperCase() : this.getCurrentLanguage().toUpperCase();
        const placeholderText = (mode === 'mobile') ? langText : this.getLanguageTextByCode(lang || this.getCurrentLanguage());
        return `<option value="" disabled selected hidden>${placeholderText}</option>`;
    }

    renderSelectOptions(nextLang) {
        const nextMode = this.getNextMode();
        if (nextMode !== this.currentMode || nextLang !== this.getCurrentLanguage()) {
            const className = this.options.languageSelectOptionClass;
            const textProp = (nextMode === 'mobile') ?
                this.options.mobileTextProperty :
                this.options.desktopTextProperty;
            let optionsHTML = `${this.getPlaceholderOption(nextLang, nextMode)}`;
            this.options.languages.forEach(item => {
                optionsHTML += `<option class="${className}" value="${item.code}">${item[textProp]}</option>`;
            });
            this.getElement().innerHTML = optionsHTML;
            this.currentMode = nextMode;
            this.updateSelectElementMaxWidth(nextLang);
        }
    }

    doTranslate(lang, retryDelay=500) {
        const selects = document.getElementsByTagName('select');

        let googleTranslateSelect;
        for (let i = 0; i < selects.length; i++) {
            if (selects[i].className === 'goog-te-combo') {
                googleTranslateSelect = selects[i];
            }
        }

        if (
            document.getElementById('google_translate_element2') == null ||
            document.getElementById('google_translate_element2').innerHTML
                .length === 0 ||
            googleTranslateSelect.length === 0 ||
            googleTranslateSelect.innerHTML.length === 0
        ) {
            setTimeout(() => {
                this.doTranslate(lang, retryDelay ^ 1.03);
            }, retryDelay);
        } else {
            googleTranslateSelect.value = lang;
            this.translateFireEvent(googleTranslateSelect, 'change');
            this.translateFireEvent(googleTranslateSelect, 'change');
        }
    }

    translateFireEvent(selectEl, eventName) {
        try {
            if (document.createEvent) {
                const ev = document.createEvent('HTMLEvents');
                ev.initEvent(eventName, true, true);
                selectEl.dispatchEvent(ev);
            } else {
                const ev = document.createEventObject();
                selectEl.fireEvent('on' + eventName, ev);
            }
        } catch (e) {}
    }

    parseOptions(userOptions) {
        this.options = _merge(DEFAULT_OPTIONS, userOptions);
    }
}
